import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Sidebar } from '../components/Sidebar';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Loader2, Save, DollarSign, Users, Clock, AlertCircle, Package, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DailyEntryForm() {
  const { user, isExecutive } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState('');
  const [budgetInfo, setBudgetInfo] = useState(null);  // Budget from executive
  const [loadingBudget, setLoadingBudget] = useState(false);
  const [entryDate, setEntryDate] = useState(new Date().toISOString().split('T')[0]);
  
  const [formData, setFormData] = useState({
    actual_total_sales: '',
    labor_hours_worked: '',
    pax: '',
    people_captured: '',
    seen: '',
    transactions: '',
    // Product Mix
    core_photo_sales: '',
    supplemental_sales: '',
    hard_product_sales: '',
    digital_product_sales: '',
    notes: ''
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    fetchLocations();
  }, []);

  // Fetch budget when location or date changes
  useEffect(() => {
    if (selectedLocation && entryDate) {
      fetchBudget();
    }
  }, [selectedLocation, entryDate]);

  const fetchLocations = async () => {
    try {
      const res = await axios.get(`${API}/locations`);
      setLocations(res.data);
      
      if (!isExecutive && user?.location_id) {
        setSelectedLocation(user.location_id);
      } else if (res.data.length > 0) {
        setSelectedLocation(res.data[0].id);
      }
    } catch (error) {
      console.error('Failed to fetch locations:', error);
    }
  };

  const fetchBudget = async () => {
    setLoadingBudget(true);
    try {
      const res = await axios.get(`${API}/budgets`, {
        params: {
          location_id: selectedLocation,
          start_date: entryDate,
          end_date: entryDate
        }
      });
      
      if (res.data.length > 0) {
        setBudgetInfo(res.data[0]);
        setErrors(prev => ({ ...prev, budget: null }));
      } else {
        setBudgetInfo(null);
        setErrors(prev => ({ ...prev, budget: 'No budget set for this date. Please contact an executive.' }));
      }
    } catch (error) {
      console.error('Failed to fetch budget:', error);
      setBudgetInfo(null);
    } finally {
      setLoadingBudget(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  // Auto-calculate total sales from product mix
  const productMixTotal = useMemo(() => {
    const core = parseFloat(formData.core_photo_sales) || 0;
    const supp = parseFloat(formData.supplemental_sales) || 0;
    return core + supp;
  }, [formData.core_photo_sales, formData.supplemental_sales]);

  const productMixMatches = useMemo(() => {
    const total = parseFloat(formData.actual_total_sales) || 0;
    return Math.abs(productMixTotal - total) < 0.01;
  }, [productMixTotal, formData.actual_total_sales]);

  const supplementalPct = useMemo(() => {
    const total = parseFloat(formData.actual_total_sales) || 0;
    const supp = parseFloat(formData.supplemental_sales) || 0;
    return total > 0 ? (supp / total * 100).toFixed(1) : '0.0';
  }, [formData.actual_total_sales, formData.supplemental_sales]);

  const validateForm = () => {
    const newErrors = {};
    
    if (!selectedLocation) newErrors.location = 'Please select a location';
    if (!budgetInfo) {
      newErrors.budget = 'No budget set for this date. Please contact an executive.';
    }
    if (!formData.actual_total_sales || parseFloat(formData.actual_total_sales) < 0) {
      newErrors.actual_total_sales = 'Sales cannot be negative';
    }
    if (!formData.labor_hours_worked || parseFloat(formData.labor_hours_worked) <= 0) {
      newErrors.labor_hours_worked = 'Labor hours must be greater than 0';
    }
    if (!formData.people_captured || parseInt(formData.people_captured) < 0) {
      newErrors.people_captured = 'Captured cannot be negative';
    }
    if (!formData.seen || parseInt(formData.seen) < 0) {
      newErrors.seen = 'Seen cannot be negative';
    }
    if (!formData.transactions || parseInt(formData.transactions) < 0) {
      newErrors.transactions = 'Transactions cannot be negative';
    }
    
    // Product Mix validation
    if (!formData.core_photo_sales || parseFloat(formData.core_photo_sales) < 0) {
      newErrors.core_photo_sales = 'Core Photo Sales required';
    }
    if (!formData.supplemental_sales || parseFloat(formData.supplemental_sales) < 0) {
      newErrors.supplemental_sales = 'Supplemental Sales required';
    }
    
    // Validate product mix total matches actual sales
    if (!productMixMatches && formData.actual_total_sales) {
      newErrors.product_mix = `Product mix (${productMixTotal.toFixed(2)}) must equal Total Sales (${formData.actual_total_sales})`;
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('Please fix the errors before submitting');
      return;
    }
    
    setLoading(true);
    
    try {
      const payload = {
        location_id: selectedLocation,
        date: entryDate,
        actual_total_sales: parseFloat(formData.actual_total_sales),
        labor_hours_worked: parseFloat(formData.labor_hours_worked),
        pax: formData.pax ? parseInt(formData.pax) : null,
        people_captured: parseInt(formData.people_captured),
        seen: parseInt(formData.seen),
        transactions: parseInt(formData.transactions),
        core_photo_sales: parseFloat(formData.core_photo_sales),
        supplemental_sales: parseFloat(formData.supplemental_sales),
        hard_product_sales: formData.hard_product_sales ? parseFloat(formData.hard_product_sales) : null,
        digital_product_sales: formData.digital_product_sales ? parseFloat(formData.digital_product_sales) : null,
        notes: formData.notes || null
      };
      
      const res = await axios.post(`${API}/entries`, payload);
      toast.success('Entry submitted successfully!');
      navigate(`/scorecards/${res.data.id}`);
    } catch (error) {
      console.error('Failed to submit entry:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit entry');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-gradient-to-br from-violet-50 via-white to-fuchsia-50">
      <Sidebar />
      
      <main className="flex-1 p-4 lg:p-8" data-testid="daily-entry-form">
        <div className="max-w-3xl mx-auto">
          <div className="mb-8">
            <h1 className="text-2xl lg:text-3xl font-bold text-slate-900 font-['Outfit']">
              Daily Entry
            </h1>
            <p className="text-slate-600 mt-1">
              Enter today's raw numbers — the system will calculate your scorecard
            </p>
          </div>

          <form onSubmit={handleSubmit}>
            {/* Location Selection */}
            {isExecutive && (
              <Card className="mb-6">
                <CardContent className="pt-6">
                  <div className="space-y-2">
                    <Label htmlFor="location">Location</Label>
                    <Select value={selectedLocation} onValueChange={setSelectedLocation}>
                      <SelectTrigger className="h-12" data-testid="location-select">
                        <SelectValue placeholder="Select a location" />
                      </SelectTrigger>
                      <SelectContent>
                        {locations.map(loc => (
                          <SelectItem key={loc.id} value={loc.id}>
                            {loc.name} ({loc.code})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {errors.location && (
                      <p className="text-sm text-red-500 flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" />
                        {errors.location}
                      </p>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Date Selection & Budget Info (Read-only) */}
            <Card className="mb-6">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-violet-600" />
                  Today's Budget
                </CardTitle>
                <CardDescription>Budget set by executive for this date</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
                  <div className="space-y-2">
                    <Label htmlFor="entryDate">Entry Date</Label>
                    <Input
                      id="entryDate"
                      type="date"
                      value={entryDate}
                      onChange={(e) => setEntryDate(e.target.value)}
                      className="h-12"
                      data-testid="input-date"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Sales Budget</Label>
                    <div className="h-12 px-4 rounded-md border border-slate-200 bg-slate-50 flex items-center font-mono text-lg text-slate-700">
                      {loadingBudget ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : budgetInfo ? (
                        `$${budgetInfo.sales_budget.toLocaleString()}`
                      ) : (
                        <span className="text-red-500 text-sm">Not set</span>
                      )}
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Labor Hours Budget</Label>
                    <div className="h-12 px-4 rounded-md border border-slate-200 bg-slate-50 flex items-center font-mono text-lg text-slate-700">
                      {loadingBudget ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : budgetInfo ? (
                        `${budgetInfo.labor_hours_budget}h`
                      ) : (
                        <span className="text-red-500 text-sm">Not set</span>
                      )}
                    </div>
                  </div>
                </div>
                
                {errors.budget && (
                  <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm flex items-center gap-2">
                    <AlertCircle className="w-4 h-4" />
                    {errors.budget}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Financial & Labor Actuals */}
            <Card className="mb-6">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Clock className="w-5 h-5 text-violet-600" />
                  Actuals
                </CardTitle>
                <CardDescription>Enter actual sales and labor hours worked</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="sales">Actual Total Sales ($)</Label>
                    <Input
                      id="sales"
                      type="number"
                      inputMode="decimal"
                      step="0.01"
                      placeholder="4800.00"
                      value={formData.actual_total_sales}
                      onChange={(e) => handleInputChange('actual_total_sales', e.target.value)}
                      className="h-12 font-mono"
                      data-testid="input-sales"
                    />
                    {errors.actual_total_sales && (
                      <p className="text-xs text-red-500">{errors.actual_total_sales}</p>
                    )}
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="labor">Labor Hours Worked</Label>
                    <Input
                      id="labor"
                      type="number"
                      inputMode="decimal"
                      step="0.5"
                      placeholder="80.5"
                      value={formData.labor_hours_worked}
                      onChange={(e) => handleInputChange('labor_hours_worked', e.target.value)}
                      className="h-12 font-mono"
                      data-testid="input-labor"
                    />
                    {errors.labor_hours_worked && (
                      <p className="text-xs text-red-500">{errors.labor_hours_worked}</p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Product Mix */}
            <Card className="mb-6">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Package className="w-5 h-5 text-violet-600" />
                  Product Mix
                </CardTitle>
                <CardDescription>Sales breakdown by product category (Core + Supplemental = Total)</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                  <div className="space-y-2">
                    <Label htmlFor="core_photo">Core Photo Package Sales ($)</Label>
                    <Input
                      id="core_photo"
                      type="number"
                      inputMode="decimal"
                      step="0.01"
                      placeholder="3600.00"
                      value={formData.core_photo_sales}
                      onChange={(e) => handleInputChange('core_photo_sales', e.target.value)}
                      className="h-12 font-mono"
                      data-testid="input-core-photo"
                    />
                    {errors.core_photo_sales && (
                      <p className="text-xs text-red-500">{errors.core_photo_sales}</p>
                    )}
                  </div>
                  
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="supplemental">Supplemental Product Sales ($)</Label>
                      {formData.actual_total_sales && !productMixMatches && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-6 text-xs text-violet-600 hover:text-violet-700"
                          onClick={() => {
                            const total = parseFloat(formData.actual_total_sales) || 0;
                            const core = parseFloat(formData.core_photo_sales) || 0;
                            const supp = Math.max(0, total - core);
                            handleInputChange('supplemental_sales', supp.toFixed(2));
                          }}
                          data-testid="quick-fill-btn"
                        >
                          Quick Fill →
                        </Button>
                      )}
                    </div>
                    <Input
                      id="supplemental"
                      type="number"
                      inputMode="decimal"
                      step="0.01"
                      placeholder="1200.00"
                      value={formData.supplemental_sales}
                      onChange={(e) => handleInputChange('supplemental_sales', e.target.value)}
                      className="h-12 font-mono"
                      data-testid="input-supplemental"
                    />
                    {errors.supplemental_sales && (
                      <p className="text-xs text-red-500">{errors.supplemental_sales}</p>
                    )}
                  </div>
                </div>

                {/* Validation indicator */}
                <div className={`p-3 rounded-lg mb-4 flex items-center justify-between ${
                  formData.actual_total_sales && productMixMatches 
                    ? 'bg-emerald-50 border border-emerald-200 text-emerald-700' 
                    : formData.actual_total_sales && !productMixMatches
                    ? 'bg-red-50 border border-red-200 text-red-700'
                    : 'bg-slate-50 border border-slate-200 text-slate-600'
                }`}>
                  <div className="flex items-center gap-2">
                    {formData.actual_total_sales && productMixMatches ? (
                      <CheckCircle2 className="w-4 h-4" />
                    ) : (
                      <AlertCircle className="w-4 h-4" />
                    )}
                    <span className="text-sm">
                      Core + Supplemental = <span className="font-mono font-semibold">${productMixTotal.toFixed(2)}</span>
                      {formData.actual_total_sales && (
                        <span className="ml-2">
                          (Total Sales: <span className="font-mono">${parseFloat(formData.actual_total_sales).toFixed(2)}</span>)
                        </span>
                      )}
                    </span>
                  </div>
                  {formData.actual_total_sales && formData.core_photo_sales && !productMixMatches && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="ml-2 text-xs"
                      onClick={() => {
                        const total = parseFloat(formData.actual_total_sales) || 0;
                        const core = parseFloat(formData.core_photo_sales) || 0;
                        const supp = Math.max(0, total - core);
                        handleInputChange('supplemental_sales', supp.toFixed(2));
                      }}
                      data-testid="quick-fill-calc-btn"
                    >
                      Calculate: ${(parseFloat(formData.actual_total_sales) - parseFloat(formData.core_photo_sales || 0)).toFixed(2)}
                    </Button>
                  )}
                </div>
                {errors.product_mix && (
                  <p className="text-sm text-red-500 mb-4">{errors.product_mix}</p>
                )}

                {/* Optional breakdown */}
                <div className="border-t pt-4 mt-2">
                  <p className="text-sm text-slate-500 mb-3">Optional: Hard vs Digital breakdown</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="hard_product">Hard Product Sales ($)</Label>
                      <Input
                        id="hard_product"
                        type="number"
                        inputMode="decimal"
                        step="0.01"
                        placeholder="400.00"
                        value={formData.hard_product_sales}
                        onChange={(e) => handleInputChange('hard_product_sales', e.target.value)}
                        className="h-12 font-mono"
                        data-testid="input-hard-product"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="digital_product">Digital Product Sales ($)</Label>
                      <Input
                        id="digital_product"
                        type="number"
                        inputMode="decimal"
                        step="0.01"
                        placeholder="800.00"
                        value={formData.digital_product_sales}
                        onChange={(e) => handleInputChange('digital_product_sales', e.target.value)}
                        className="h-12 font-mono"
                        data-testid="input-digital-product"
                      />
                    </div>
                  </div>
                </div>

                {/* Supplemental % indicator */}
                {formData.actual_total_sales && formData.supplemental_sales && (
                  <div className="mt-4 p-3 rounded-lg bg-violet-50 border border-violet-200">
                    <span className="text-sm text-violet-700">
                      Supplemental Sales: <span className="font-mono font-bold">{supplementalPct}%</span> of total
                      <span className="ml-2 text-violet-500">(Target: 25%)</span>
                    </span>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Guest & Capture */}
            <Card className="mb-6">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Users className="w-5 h-5 text-violet-600" />
                  Guest & Capture
                </CardTitle>
                <CardDescription>Visitor tracking and engagement metrics</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="pax" className="flex items-center gap-1">
                      PAX
                      <span className="text-xs text-slate-400">(optional)</span>
                    </Label>
                    <Input
                      id="pax"
                      type="number"
                      inputMode="numeric"
                      placeholder="1200"
                      value={formData.pax}
                      onChange={(e) => handleInputChange('pax', e.target.value)}
                      className="h-12 font-mono"
                      data-testid="input-pax"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="captured">People Captured</Label>
                    <Input
                      id="captured"
                      type="number"
                      inputMode="numeric"
                      placeholder="800"
                      value={formData.people_captured}
                      onChange={(e) => handleInputChange('people_captured', e.target.value)}
                      className="h-12 font-mono"
                      data-testid="input-captured"
                    />
                    {errors.people_captured && (
                      <p className="text-xs text-red-500">{errors.people_captured}</p>
                    )}
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="seen">Seen (Kiosk Views)</Label>
                    <Input
                      id="seen"
                      type="number"
                      inputMode="numeric"
                      placeholder="500"
                      value={formData.seen}
                      onChange={(e) => handleInputChange('seen', e.target.value)}
                      className="h-12 font-mono"
                      data-testid="input-seen"
                    />
                    {errors.seen && (
                      <p className="text-xs text-red-500">{errors.seen}</p>
                    )}
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="transactions">Transactions</Label>
                    <Input
                      id="transactions"
                      type="number"
                      inputMode="numeric"
                      placeholder="150"
                      value={formData.transactions}
                      onChange={(e) => handleInputChange('transactions', e.target.value)}
                      className="h-12 font-mono"
                      data-testid="input-transactions"
                    />
                    {errors.transactions && (
                      <p className="text-xs text-red-500">{errors.transactions}</p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Notes */}
            <Card className="mb-6">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Clock className="w-5 h-5 text-violet-600" />
                  Notes
                </CardTitle>
                <CardDescription>Optional context for today's performance</CardDescription>
              </CardHeader>
              <CardContent>
                <Textarea
                  placeholder="Any relevant notes about today's operations..."
                  value={formData.notes}
                  onChange={(e) => handleInputChange('notes', e.target.value)}
                  rows={3}
                  data-testid="input-notes"
                />
              </CardContent>
            </Card>

            {/* Submit */}
            <Button
              type="submit"
              className="w-full h-14 text-lg bg-violet-600 hover:bg-violet-700"
              disabled={loading}
              data-testid="submit-entry-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <Save className="mr-2 h-5 w-5" />
                  Submit Entry & Generate Scorecard
                </>
              )}
            </Button>
          </form>
        </div>
      </main>
    </div>
  );
}
